<template>
  <div class="overflow-x-auto table-scroll">
    <table class="w-full border-collapse">
      <thead>
        <tr>
          <th
            v-for="col in columns"
            :key="col.key"
            @click="sortBy(col.key)"
            class="bg-gray-50 px-3 py-2.5 text-left font-semibold cursor-pointer whitespace-nowrap text-gray-500 border-b-2 border-gray-200 select-none text-xs uppercase tracking-wide hover:bg-gray-100 hover:text-gray-700 transition-colors"
            :class="col.type === 'number' || col.type === 'percent' || col.type === 'duration' ? 'text-right' : ''"
          >
            <span class="flex items-center gap-1" :class="col.type === 'number' || col.type === 'percent' || col.type === 'duration' ? 'justify-end' : ''">
              {{ col.label }}
              <span
                :class="sortColumn === col.key ? 'opacity-100 text-indigo-500' : 'opacity-0'"
                class="text-xs transition-opacity"
              >
                {{ sortDir === 'asc' ? '↑' : '↓' }}
              </span>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, idx) in sortedRows"
          :key="idx"
          :class="idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
          class="hover:bg-indigo-50 transition-colors"
        >
          <td
            v-for="col in columns"
            :key="col.key"
            :class="col.type === 'number' || col.type === 'percent' || col.type === 'duration' ? 'text-right font-medium text-gray-800 num-cell' : 'text-gray-700'"
            class="px-3 py-2 border-b border-gray-100 text-xs"
          >
            {{ formatCell(row[col.key], col.type) }}
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="rows.length > displayLimit" class="text-xs text-gray-400 text-center py-1.5 m-0 border-t border-gray-100">
      {{ $t('ai.table.showingRows', { limit: displayLimit, total: rows.length }) }}
    </p>
  </div>
</template>

<script>
export default {
  name: 'AiTable',

  props: {
    data:  { type: [Array, Object], required: true },
    title: { type: String, default: '' },
  },

  data() {
    return {
      sortColumn: null,
      sortDir: 'asc',
      displayLimit: 50,
    };
  },

  computed: {
    rows() {
      if (Array.isArray(this.data)) return this.data;
      if (this.data?.rows) return this.data.rows;
      if (this.data?.data && Array.isArray(this.data.data)) return this.data.data;
      return [this.data];
    },

    // Use typed column definitions when provided, otherwise derive from row keys
    columns() {
      if (this.data?.columns?.length) {
        return this.data.columns.map((c) => ({
          key: c.key,
          label: c.label || this.keyToLabel(c.key),
          type: c.type || 'text',
        }));
      }
      if (!this.rows.length) return [];
      return Object.keys(this.rows[0]).map((k) => ({
        key: k,
        label: this.keyToLabel(k),
        type: 'text',
      }));
    },

    sortedRows() {
      let rows = this.rows.slice(0, this.displayLimit);
      if (this.sortColumn) {
        rows = [...rows].sort((a, b) => {
          const va = a[this.sortColumn];
          const vb = b[this.sortColumn];
          const na = parseFloat(va);
          const nb = parseFloat(vb);
          const cmp = (!isNaN(na) && !isNaN(nb)) ? na - nb : String(va).localeCompare(String(vb));
          return this.sortDir === 'asc' ? cmp : -cmp;
        });
      }
      return rows;
    },
  },

  methods: {
    sortBy(key) {
      if (this.sortColumn === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = key;
        this.sortDir = 'asc';
      }
    },

    keyToLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },

    formatCell(val, type) {
      if (val === null || val === undefined) return '—';
      if (type === 'duration') {
        const secs = parseInt(val, 10);
        if (isNaN(secs)) return val;
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
      }
      if (type === 'percent') {
        const n = parseFloat(val);
        return isNaN(n) ? val : `${n % 1 === 0 ? n : n.toFixed(1)}%`;
      }
      if (type === 'number' || typeof val === 'number') {
        const n = typeof val === 'number' ? val : parseFloat(val);
        if (isNaN(n)) return val;
        if (n >= 1000) return n.toLocaleString();
        return n % 1 === 0 ? String(n) : n.toFixed(2);
      }
      return val;
    },
  },
};
</script>

<style scoped>
.num-cell { font-variant-numeric: tabular-nums; }
.table-scroll::-webkit-scrollbar { height: 4px; }
.table-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
</style>